/**
 * React glue for the analytics runtime (`src/lib/analytics/*`).
 *
 * - {@link AnalyticsRuntime} (mounted once in the AppShell): starts the lazy
 *   PostHog runtime with the stored consent, follows consent changes live,
 *   tracks generic CTAs (`data-cta` attributes, one delegated listener) and
 *   reports the document's navigation timing (TTFB) once.
 * - `AnalyticsIdentity` (`analytics-identity.tsx`, mounted inside Clerk +
 *   Convex in `providers.tsx`): identify / reset.
 * - {@link TrackOnMount}: fire one typed event when a view mounts.
 *
 * All render nothing.
 */
import { useEffect, useRef } from 'react'
import { trackEvent } from '../lib/analytics'
import type { ClientAnalyticsEvents, ClientEventName } from '../lib/analytics'
import { CTA_KINDS, CTA_LOCATIONS } from '../lib/analytics/events'
import type { CtaKind, CtaLocation } from '../lib/analytics/events'
import { resolveAnalyticsMode } from '../lib/analytics/consent-policy'
import {
  setAnalyticsMode,
  startAnalyticsRuntime,
} from '../lib/analytics/posthog-client'
import { onConsentChange, readStoredConsent } from '../lib/consent-events'
import { getGpcSnapshot } from '../lib/use-cookie-consent'

/** Read the CTA descriptor of a clicked element (pure, tested). */
export function ctaFromElement(
  element: Element | null,
): { location: CtaLocation; cta: CtaKind; href: string } | null {
  const target = element?.closest<HTMLElement>('[data-cta]')
  if (!target) return null
  const location = target.dataset.cta as CtaLocation | undefined
  const cta = target.dataset.ctaKind as CtaKind | undefined
  if (!location || !CTA_LOCATIONS.includes(location)) return null
  if (!cta || !CTA_KINDS.includes(cta)) return null
  let href = target.getAttribute('href') ?? ''
  try {
    if (href) href = new URL(href, window.location.origin).pathname
  } catch {
    // keep raw
  }
  return { location, cta, href }
}

/** Once per document load (effects can re-run, e.g. StrictMode in dev). */
let navigationTimingReported = false

function reportNavigationTiming() {
  if (navigationTimingReported) return
  navigationTimingReported = true
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined
    if (!nav) return
    trackEvent('navigation_timing', {
      ttfb_ms: Math.round(nav.responseStart - nav.startTime),
      dom_content_loaded_ms: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
      load_ms: nav.loadEventEnd > 0 ? Math.round(nav.loadEventEnd - nav.startTime) : null,
      transfer_kb: nav.transferSize > 0 ? Math.round(nav.transferSize / 1024) : null,
      navigation_type: nav.type,
    })
  } catch {
    // unsupported browser
  }
}

export function AnalyticsRuntime() {
  useEffect(() => {
    startAnalyticsRuntime(
      resolveAnalyticsMode({ storedConsent: readStoredConsent(), gpc: getGpcSnapshot() }),
    )
    const stopConsent = onConsentChange((consent) => {
      setAnalyticsMode(
        resolveAnalyticsMode({ storedConsent: consent, gpc: getGpcSnapshot() }),
      )
    })
    const onClick = (event: MouseEvent) => {
      const cta = ctaFromElement(event.target as Element | null)
      if (cta) trackEvent('cta_clicked', cta)
    }
    document.addEventListener('click', onClick, { capture: true })

    const onLoad = () => window.setTimeout(reportNavigationTiming, 0)
    if (document.readyState === 'complete') onLoad()
    else window.addEventListener('load', onLoad, { once: true })

    return () => {
      stopConsent()
      document.removeEventListener('click', onClick, { capture: true })
      window.removeEventListener('load', onLoad)
    }
  }, [])
  return null
}

/** Fire `name` once per mount (and again when `dedupeKey` changes). */
export function TrackOnMount<TName extends ClientEventName>({
  name,
  props,
  dedupeKey,
}: {
  name: TName
  props: ClientAnalyticsEvents[TName]
  dedupeKey: string
}) {
  const propsRef = useRef(props)
  propsRef.current = props
  useEffect(() => {
    trackEvent(name, propsRef.current)
  }, [name, dedupeKey])
  return null
}
